// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo, useState } from 'react';
import "./PageViewAnalytics.scss";
import { useTranslation } from "react-i18next";
import { useTable, useSortBy, useExpanded, usePagination } from 'react-table';
import { format } from "date-fns";
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function PageViewAnalytics({ logs, users, getUserDisplayName }) {
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState(10);

  // Process logs to extract page_view events and their page_title
  const processedData = useMemo(() => {
    // Filter only page_view events that have page_title parameter
    const pageViewLogs = logs.filter(log => 
      log.event_name === "page_view" && 
      log.event_params && 
      log.event_params.page_title
    );

    if (pageViewLogs.length === 0) {
      return {
        pageGroups: [],
        pageCount: {},
        totalEvents: 0
      };
    }

    // Group by page_title
    const groupedByPage = {};
    const pageCount = {};

    pageViewLogs.forEach(log => {
      const pageTitle = log.event_params.page_title;
      
      // Initialize groups if needed
      if (!groupedByPage[pageTitle]) {
        groupedByPage[pageTitle] = {
          pageTitle,
          events: [],
          users: new Set(),
          sources: new Set()
        };
      }
      
      // Add log to group
      groupedByPage[pageTitle].events.push(log);
      groupedByPage[pageTitle].users.add(log.user);
      groupedByPage[pageTitle].sources.add(log.source);
      
      // Count pages
      pageCount[pageTitle] = (pageCount[pageTitle] || 0) + 1;
    });

    // Convert to array and finalize data
    const pageGroups = Object.values(groupedByPage).map(group => ({
      ...group,
      userCount: group.users.size,
      users: Array.from(group.users),
      sourceCount: group.sources.size,
      sources: Array.from(group.sources),
      viewCount: group.events.length
    })).sort((a, b) => b.viewCount - a.viewCount);

    return {
      pageGroups,
      pageCount,
      totalEvents: pageViewLogs.length
    };
  }, [logs]);

  // Chart data for top pages
  const chartData = useMemo(() => {
    if (processedData.pageGroups.length === 0) {
      return {
        labels: [],
        datasets: [{ data: [] }]
      };
    }

    // Limit to top 10 pages for chart visibility
    const topPages = [...processedData.pageGroups].slice(0, 10);
    
    const labels = topPages.map(page => {
      // Truncate long titles for better display
      const title = page.pageTitle;
      return title.length > 25 ? title.substring(0, 22) + '...' : title;
    });
    
    const data = topPages.map(page => page.viewCount);
    
    return {
      labels,
      datasets: [
        {
          label: t('page_views') || 'Page Views',
          data,
          backgroundColor: 'rgba(66, 144, 245, 0.7)',
          borderColor: 'rgba(66, 144, 245, 1)',
          borderWidth: 1
        }
      ]
    };
  }, [processedData.pageGroups, t]);

  // Table columns definition
  const columns = useMemo(() => [
    {
      Header: t('page_title') || 'Page Title',
      accessor: 'pageTitle',
      width: 300,
      Cell: ({ value }) => (
        <div className="page-title">
          {value}
        </div>
      )
    },
    {
      Header: t('views') || 'Views',
      accessor: 'viewCount',
      width: 100,
      Cell: ({ value }) => (
        <div className="view-count">
          <span className="count-badge">{value}</span>
        </div>
      )
    },
    {
      Header: t('unique_users') || 'Unique Users',
      accessor: 'userCount',
      width: 150,
      Cell: ({ value }) => (
        <div className="user-count">
          <span className="count-badge users">{value}</span>
        </div>
      )
    },
    {
      Header: t('sources') || 'Sources',
      accessor: 'sources',
      Cell: ({ value }) => (
        <div className="sources-container">
          {value.map(source => (
            <span 
              key={source} 
              className={`source-badge ${source}`}
            >
              {source}
            </span>
          ))}
        </div>
      )
    }
  ], [t]);

  // Row expansion details
  const renderRowSubComponent = React.useCallback(({ row }) => {
    // Group users by number of views
    const userViewCounts = {};
    row.original.events.forEach(event => {
      userViewCounts[event.user] = (userViewCounts[event.user] || 0) + 1;
    });
    
    const userViewData = Object.entries(userViewCounts)
      .map(([userId, count]) => ({
        userId,
        userName: getUserDisplayName(userId),
        count
      }))
      .sort((a, b) => b.count - a.count);
    
    return (
      <div className="page-view-detail">
        <h4>{t('users_who_viewed') || `Users who viewed: ${row.original.pageTitle}`}</h4>
        <div className="user-list">
          {userViewData.map(userData => (
            <div key={userData.userId} className="user-item">
              <div className="user-name">{userData.userName}</div>
              <div className="view-count-badge">
                {userData.count} {userData.count === 1 ? 'view' : 'views'}
              </div>
            </div>
          ))}
        </div>
        
        <h4>{t('recent_page_views') || 'Recent Page Views'}</h4>
        <table className="views-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>Source</th>
              <th>Parameters</th>
            </tr>
          </thead>
          <tbody>
            {[...row.original.events]
              .sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate())
              .slice(0, 10)
              .map((event, idx) => (
                <tr key={idx}>
                  <td className="time-cell">{format(event.timestamp.toDate(), "MMM dd, yyyy HH:mm:ss")}</td>
                  <td className="user-cell">{getUserDisplayName(event.user)}</td>
                  <td className="source-cell">{event.source}</td>
                  <td className="params-cell">
                    {event.event_params && Object.keys(event.event_params).length > 0 ? (
                      <div className="params-grid">
                        {Object.entries(event.event_params).map(([key, value]) => (
                          <div key={key} className="param-item">
                            <span className="param-key">{key}:</span>
                            <span className="param-value">
                              {typeof value === 'object' 
                                ? JSON.stringify(value) 
                                : String(value)
                              }
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="no-params">No parameters</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }, [getUserDisplayName, t]);

  // Initialize table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    visibleColumns,
    state: { expanded, pageIndex },
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize: setTablePageSize,
  } = useTable(
    { 
      columns, 
      data: processedData.pageGroups,
      initialState: {
        sortBy: [{ id: 'viewCount', desc: true }],
        pageIndex: 0,
        pageSize
      },
      autoResetExpanded: false
    },
    useSortBy,
    useExpanded,
    usePagination
  );

  const handlePageSizeChange = (e) => {
    const newPageSize = Number(e.target.value);
    setPageSize(newPageSize);
    setTablePageSize(newPageSize);
  };

  // Empty state handling
  if (processedData.pageGroups.length === 0) {
    return (
      <div className="page-view-analytics empty-state">
        <h3>{t('page_view_analytics') || 'Page View Analytics'}</h3>
        <p>{t('no_page_view_events') || 'No page_view events with page_title parameter found in the selected logs.'}</p>
      </div>
    );
  }

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#24292e',
        bodyColor: '#24292e',
        borderColor: '#eaecef',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          title: function(context) {
            // Display full page title in tooltip
            const dataIndex = context[0].dataIndex;
            return processedData.pageGroups[dataIndex]?.pageTitle || '';
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };

  return (
    <div className="page-view-analytics">
      <h3>{t('page_view_analytics') || 'Page View Analytics'}</h3>
      
      <div className="page-analytics-header">
        <div className="page-summary">
          <div className="summary-item">
            <div className="summary-label">{t('total_page_views') || 'Total Page Views'}</div>
            <div className="summary-value">{processedData.totalEvents}</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">{t('unique_pages') || 'Unique Pages'}</div>
            <div className="summary-value">{processedData.pageGroups.length}</div>
          </div>
        </div>
        
        <div className="chart-container">
          <h4>{t('top_pages') || 'Top Pages'}</h4>
          <div className="chart-inner">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      </div>
      
      <div className="table-wrapper">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                <th className="expander-column"></th>
                {headerGroup.headers.map(column => (
                  <th 
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={column.isSorted ? (column.isSortedDesc ? 'sorted-desc' : 'sorted-asc') : ''}
                  >
                    {column.render('Header')}
                    <span className="sort-indicator">
                      {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row);
              const isExpanded = expanded[row.id];
              return (
                <React.Fragment key={row.getRowProps().key}>
                  <tr {...row.getRowProps()} className={isExpanded ? 'row-expanded' : ''}>
                    <td className="expander-column">
                      <button 
                        {...row.getToggleRowExpandedProps()}
                        className={`expander-button ${isExpanded ? 'expanded' : 'collapsed'}`}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                    </td>
                    {row.cells.map(cell => (
                      <td {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className="expanded-row">
                      <td colSpan={visibleColumns.length + 1}>
                        {renderRowSubComponent({ row })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <div className="pagination">
        <div className="pagination-controls">
          <button 
            onClick={() => gotoPage(0)} 
            disabled={!canPreviousPage}
            className="pagination-button"
          >
            {'<<'}
          </button>
          <button 
            onClick={() => previousPage()} 
            disabled={!canPreviousPage}
            className="pagination-button"
          >
            {'<'}
          </button>
          <span className="pagination-info">
            {t('page') || 'Page'}{' '}
            <strong>
              {pageIndex + 1} {t('of') || 'of'} {pageOptions.length}
            </strong>{' '}
          </span>
          <button 
            onClick={() => nextPage()} 
            disabled={!canNextPage}
            className="pagination-button"
          >
            {'>'}
          </button>
          <button 
            onClick={() => gotoPage(pageCount - 1)} 
            disabled={!canNextPage}
            className="pagination-button"
          >
            {'>>'}
          </button>
        </div>
        <div className="page-size-selector">
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="page-size-select"
          >
            {[10, 20, 30, 40, 50].map(size => (
              <option key={size} value={size}>
                {t('show') || 'Show'} {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
} 